import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, SkeletonPlaceholder } from '@carbon/react';
import { Reset } from '@carbon/react/icons';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import {
  NumericAxis,
  EAxisAlignment,
  NumberRange,
  BoxAnnotation,
  TextAnnotation,
  LineAnnotation,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  ChartModifierBase2D,
  DpiHelper,
  EExecuteOn,
  EModifierMouseArgKey,
  EXyDirection,
  MouseWheelZoomModifier,
  Thickness,
  ZoomPanModifier,
  translateFromCanvasToSeriesViewRect,
  type ModifierMouseArgs,
  type Point,
  type Rect,
} from 'scichart';
import {
  ExportImageButton,
  type ChartImageExporter,
  type OutputFormat,
} from 'components/chart-export';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart/scichart-view';
import { closeExportImageForAtom } from '../../atoms';
import type {
  ChartExportConfig,
  Tone,
  WaterfallBar,
  WaterfallColumnExtent,
} from '../../types';
import {
  dollarsDeltaSign,
  formatBarChange,
  formatMillions,
  projectWaterfall,
  VALUE_AXIS_PAD_ABOVE,
  VALUE_AXIS_PAD_BELOW,
  visibleValueRange,
  waterfallAxisLabel,
  waterfallValueAxisLabel,
} from '../../utils';
import { isSlotWaterfall } from '../../slot-reveal-utils';
import styles from './npv-waterfall.module.scss';

/** Concrete hex fills (SciChart draws on WebGL — no CSS tokens). Carbon palette. */
const TONE_COLORS: Record<Tone, string> = {
  absolute: '#0f62fe', // blue-60 — the Origin/Target anchors
  positive: '#24a148', // green-50 — a value increase
  negative: '#da1e28', // red-60 — a value decrease
  neutral: '#8d8d8d', // gray-50 — a zero/unsigned delta (e.g. Unexplained ≈ 0)
};

const CONNECTOR_COLOR = '#c6c6c6';
const AXIS_ID_X = 'pec-waterfall-x';
const AXIS_ID_Y = 'pec-waterfall-y';

/** The image formats the export offers, as the package page's content export does. */
const EXPORT_FORMATS: OutputFormat[] = ['png', 'jpeg'];

/** Half the column width, in x data units (bars sit on integer x = 0,1,2…). */
const HALF_BAR_WIDTH = 0.34;

/**
 * Slot bars' x-axis labels stand upright, reading bottom to top. Upright, a label takes only its
 * text height along the axis, so neighbours fit where a diagonal label's bounding box would hide
 * every other one.
 */
export const ROTATED_LABEL_DEGREES = -90;

/** The canvas height upright labels add (a shortened well name, its tick and padding), so the plot keeps its size. */
export const ROTATED_LABEL_BAND_PX = 140;

/**
 * Above this many columns — the two anchors included — the chart thins (no connectors, no value
 * labels; the tooltip gives each value) and zooms, its value axis following the bars in view.
 */
export const DENSE_COLUMN_COUNT = 20;

/** The empty room an extendable chart's view may run past its last bar, in bars. */
const EXTEND_ROOM_COLUMNS = 2;

/** How far into that room (in bars) the view's right edge must go to ask for more bars. */
const EXTEND_TRIGGER_COLUMNS = 0.5;

/** A view counts as zoomed once an edge moves more than this share of its span off home. */
const ZOOM_TOLERANCE = 0.001;

/** A view change that keeps its span to within this share of it is a drag, not a zoom. */
const DRAG_SPAN_TOLERANCE = 1e-6;

/**
 * One projected column: the telescoped bar geometry in $M plus its color tone and
 * drill affordance. `startM`/`runningM` bracket the floating bar; `loM`/`hiM` are the
 * drawn rectangle bounds.
 */
interface WaterfallColumn extends WaterfallColumnExtent {
  key: string;
  label: string;
  drillable: boolean;
  /** Cumulative running total BEFORE this bar ($M); 0 for the absolute anchors. */
  startM: number;
  /** The bar's own value ($M) — absolute for anchors, signed delta otherwise. */
  valueM: number;
}

/**
 * Projects the raw dollar bars into telescoped $M columns. `absolute` bars (Origin,
 * Target) are grounded at zero; `delta` bars float on the running cumulative (each
 * starts where the previous left off). Pure — safe to unit-test.
 *
 * @param {WaterfallBar[]} bars - The waterfall bars in display order (raw dollars).
 * @param {string[]} [drillableKeys] - Keys of bars the user can drill into.
 * @return {WaterfallColumn[]} The positioned columns in $M.
 */
export function buildWaterfallColumns(
  bars: WaterfallBar[],
  drillableKeys: string[] = []
): WaterfallColumn[] {
  // Reuse the shared running-cumulative walk (dollars) and scale it to $M, so the chart
  // and the Summary Table's rows derive from one computation.
  return projectWaterfall(bars).map((bar) => {
    const startM = bar.origin / 1_000_000;
    const runningM = bar.target / 1_000_000;
    const valueM = bar.value / 1_000_000;
    return {
      key: bar.key,
      label: bar.label,
      // As displayed (formatBarChange): only a change that reads as $0 is neutral, like its
      // unsigned label — a +$12.0K bar is toned as the change it is.
      tone: bar.type === 'absolute' ? 'absolute' : dollarsDeltaSign(bar.value),
      drillable: drillableKeys.includes(bar.key),
      startM,
      runningM,
      loM: Math.min(startM, runningM),
      hiM: Math.max(startM, runningM),
      valueM,
    };
  });
}

/**
 * How a waterfall lays out for its bars: a slot drill-down stands its labels upright (well names
 * are long and many), and a chart of more than {@link DENSE_COLUMN_COUNT} bars thins. Pure.
 *
 * @param {{ key: string }[]} columns - The chart's bars (or columns).
 * @return {{ rotated: boolean, dense: boolean }} Whether labels stand upright, and whether to thin.
 */
function waterfallLayout(columns: { key: string }[]): {
  rotated: boolean;
  dense: boolean;
} {
  return {
    rotated: isSlotWaterfall(columns),
    dense: columns.length > DENSE_COLUMN_COUNT,
  };
}

/** A chart's x-only view modifiers: the mouse-wheel zoom, and the drag (with its pinch zoom). */
interface ViewModifiers {
  wheelZoom: MouseWheelZoomModifier;
  pan: ZoomPanModifier;
}

/**
 * Switches a chart's zoom and drag on for what it shows. Only a dense chart zooms — by Ctrl + wheel
 * (as its hint says; a trackpad pinch sends the same) or a touch pinch; a plain wheel always scrolls
 * the page, even over a chart a reveal has made dense. A chart with more bars than its view, or more
 * to come, drags along x.
 *
 * @param {object | null} modifiers - The chart's wheel-zoom and drag modifiers (null before init).
 * @param {MouseWheelZoomModifier} modifiers.wheelZoom - The wheel zoom.
 * @param {ZoomPanModifier} modifiers.pan - The drag (and pinch zoom).
 * @param {boolean} dense - The chart is dense.
 * @param {boolean} pannable - The chart has more than its view, or more to come.
 */
function setViewInteraction(
  modifiers: ViewModifiers | null,
  dense: boolean,
  pannable: boolean
): void {
  if (!modifiers) return;
  modifiers.wheelZoom.isEnabled = dense;
  modifiers.pan.isEnabled = pannable;
  modifiers.pan.enableZoom = dense;
}

/**
 * The x range that shows the first `count` bars whole: bars sit on integer x, so the range pads
 * past the first and last bar's half-width. Pure.
 *
 * @param {number} count - How many bars, from the first, to show.
 * @return {NumberRange} The x range.
 */
function waterfallXRange(count: number): NumberRange {
  return new NumberRange(-0.6, count - 0.4);
}

/**
 * The x range a chart pans within: its bars — and, for an extendable chart, room past the last one
 * to drag into. Pure.
 *
 * @param {number} count - How many bars.
 * @param {boolean} extendable - Whether the chart can grow.
 * @return {NumberRange} The range limit.
 */
function panLimit(count: number, extendable: boolean): NumberRange {
  const bars = waterfallXRange(count);
  return extendable
    ? new NumberRange(bars.min, bars.max + EXTEND_ROOM_COLUMNS)
    : bars;
}

/**
 * Whether a view's right edge has gone past the last of `count` bars, far enough into the room past
 * it to ask for more. Pure.
 *
 * @param {number} viewMax - The view's right edge, in x data units.
 * @param {number} count - How many bars.
 * @return {boolean} True once the edge is past the end.
 */
function pastTheEnd(viewMax: number, count: number): boolean {
  return viewMax > waterfallXRange(count).max + EXTEND_TRIGGER_COLUMNS;
}

/**
 * Resolves a bar index from an x data value, rejecting the pointer when it lands in the
 * gap between bars. Bars sit on integer x but are only `HALF_BAR_WIDTH` wide, so
 * rounding alone would treat the whole ±0.5 cell (incl. the whitespace either side) as a
 * hit and snap a between-bars click to the nearest bar. Pure — unit-tested.
 *
 * @param {number} dataX - The pointer's x, in data units.
 * @param {number} columnCount - The number of bars.
 * @return {number | null} The bar index under the pointer, or null when off any bar.
 */
export function columnIndexAtDataX(
  dataX: number,
  columnCount: number
): number | null {
  const index = Math.round(dataX);
  if (index < 0 || index >= columnCount) return null;
  if (Math.abs(dataX - index) > HALF_BAR_WIDTH) return null;
  return index;
}

/**
 * Resolves the bar under the pointer. SciChart hands a modifier the pointer in canvas pixels, but
 * the x-axis maps plot-area pixels — the canvas minus the axes around it — so the point is first
 * translated into the plot area, as SciChart's own modifiers do. A pointer outside the plot area
 * (over an axis) is off every bar. Pure — unit-tested.
 *
 * @param {Point} canvasPoint - The pointer, in canvas pixels (a modifier's `mousePoint`).
 * @param {Rect} seriesViewRect - The plot area, in canvas pixels.
 * @param {Function} dataXAt - Maps a plot-area x to an x data value (the x-axis calculator).
 * @param {number} columnCount - The number of bars.
 * @return {number | null} The bar index under the pointer, or null when off any bar.
 */
export function columnIndexAtPoint(
  canvasPoint: Point,
  seriesViewRect: Rect,
  dataXAt: (plotX: number) => number,
  columnCount: number
): number | null {
  const plotPoint = translateFromCanvasToSeriesViewRect(
    canvasPoint,
    seriesViewRect
  );
  if (!plotPoint) return null;
  return columnIndexAtDataX(dataXAt(plotPoint.x), columnCount);
}

// Surface interaction for the waterfall: finds the bar under the pointer (columnIndexAtPoint),
// firing a drill when a drillable bar is left-clicked and a hover callback for the tooltip. Reads
// the live columns through a getter so it never goes stale between renders. Exported for its unit
// test.
export class BarInteractionModifier extends ChartModifierBase2D {
  readonly type = 'PecBarInteractionModifier';

  getColumns: () => WaterfallColumn[] = () => [];
  onDrill: (key: string) => void = () => {};
  /** Fired with the bar under the pointer (or null) and the pointer in CSS pixels. */
  onHover: (index: number | null, x: number, y: number) => void = () => {};

  /**
   * The bar under the pointer.
   *
   * @param {ModifierMouseArgs} args - The pointer event.
   * @return {number | null} The bar index, or null when off any bar.
   */
  private indexAt(args: ModifierMouseArgs): number | null {
    const surface = this.parentSurface;
    const xAxis = surface?.xAxes.getById(AXIS_ID_X);
    if (!surface || !xAxis) return null;
    const calculator = xAxis.getCurrentCoordinateCalculator();
    return columnIndexAtPoint(
      args.mousePoint,
      surface.seriesViewRect,
      (plotX) => calculator.getDataValue(plotX),
      this.getColumns().length
    );
  }

  /**
   * Drills into a drillable bar on a left click (a right or middle click is not a drill).
   *
   * @param {ModifierMouseArgs} args - The pointer event.
   */
  override modifierMouseDown(args: ModifierMouseArgs): void {
    super.modifierMouseDown(args);
    if (args.button !== EExecuteOn.MouseLeftButton) return;
    const index = this.indexAt(args);
    if (index === null) return;
    const column = this.getColumns()[index];
    if (column?.drillable) {
      this.onDrill(column.key);
      args.handled = true;
    }
  }

  /**
   * Reports the bar under the pointer for the tooltip, which is laid out in CSS pixels — SciChart's
   * pointer is in device pixels (scaled by the display's pixel ratio).
   *
   * @param {ModifierMouseArgs} args - The pointer event.
   */
  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    const pixelRatio = DpiHelper.PIXEL_RATIO || 1;
    this.onHover(
      this.indexAt(args),
      args.mousePoint.x / pixelRatio,
      args.mousePoint.y / pixelRatio
    );
  }

  /**
   * Clears the tooltip once the pointer leaves the chart.
   *
   * @param {ModifierMouseArgs} args - The pointer event.
   */
  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    this.onHover(null, 0, 0);
  }
}

interface HoverState {
  index: number;
  x: number;
  y: number;
}

export interface NpvWaterfallProps {
  /** The waterfall bars from the run (Origin, factor deltas, Unexplained, Target). */
  bars: WaterfallBar[];
  /** Keys of bars that can be drilled into (renders a ▾ affordance + click). */
  drillableKeys?: string[];
  /** Fired with the bar key when a drillable bar is clicked (or its keyboard drill pressed). */
  onDrill?: (key: string) => void;
  /** Chart canvas height in px. */
  height?: number;
  /**
   * The run is still calculating: render a same-height skeleton in place of the chart, so no
   * partial bars ever show (default false).
   */
  loading?: boolean;
  /**
   * When provided, renders an icon-only image-export trigger (top-right) that opens the page's
   * Export Image drawer for this chart: `label` titles the export, `filenamePrefix` starts its
   * filename. Omit to render no trigger.
   */
  exportConfig?: ChartExportConfig;
  /**
   * Open on the first N bars — a window the user pans along, and where Reset zoom returns — instead
   * of all of them, keeping the user's view as bars are added. Omit to always show every bar.
   */
  initialVisibleCount?: number;
  /**
   * The chart can grow: it pans from the start, and its view may run {@link EXTEND_ROOM_COLUMNS}
   * bars past the last one, so a drag past the end is something it can notice (SciChart clamps a
   * pan to the range limit and reports no change there).
   */
  extendable?: boolean;
  /**
   * With `extendable`, fired once each time a drag takes the view's right edge into the room past
   * the last bar — one drag past the end, one call (a zoom into the room waits for the next drag);
   * the caller adds bars, which moves the end on.
   */
  onExtend?: () => void;
  /**
   * Which window the bars belong to (read with `initialVisibleCount`): bars arriving under the same
   * key — more of the same list — keep the user's view; a new key (the same level's bars for another
   * category, or in another order) opens on the window afresh.
   */
  windowKey?: string;
}

/**
 * The NPV value-driver waterfall, rendered with SciChart: Origin anchored at zero,
 * each factor's contribution floated in order, closing on Unexplained and the Target
 * anchor. Bars are colored by tone (blue anchors, green/red deltas), joined by
 * connector lines, and labeled with their $M value; drillable bars carry a ▾ and
 * drill on click. The bars are canvas pixels a keyboard can't reach, so each drillable bar also
 * gets a "Drill into …" button, hidden until it's focused. With `exportConfig`, an export trigger
 * opens the page's Export Image drawer for the chart, and the drawer is closed again once that
 * export goes stale. A slot drill-down stands its x-axis labels upright; a dense chart thins (no
 * connectors, no value labels — the tooltip gives each value), zooms (Ctrl + wheel, or pinch) and
 * pans (drag) along x with its value axis following the bars in view, and offers Reset zoom. A
 * plain wheel always scrolls the page; a chart that isn't dense only drags. An extendable chart
 * (`extendable`) pans from the start and asks for more bars (`onExtend`) each time it's dragged
 * past its end. The bars come from the run; the drawer is the only state it touches.
 *
 * @param {NpvWaterfallProps} props - The bars, drill affordances, sizing, loading state, export
 *   config, and window / extend wiring.
 * @return {JSX.Element | null} The chart, its skeleton while loading, or null when there are
 *   no bars.
 */
export function NpvWaterfall({
  bars,
  drillableKeys = [],
  onDrill,
  height = 420,
  loading = false,
  exportConfig,
  initialVisibleCount,
  extendable = false,
  onExtend,
  windowKey,
}: NpvWaterfallProps) {
  const columns = useMemo(
    () => buildWaterfallColumns(bars, drillableKeys),
    [bars, drillableKeys]
  );

  // Live refs so the once-created SciChart modifier and label providers always read
  // the current columns/callback without rebuilding the surface.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const onDrillRef = useRef(onDrill);
  onDrillRef.current = onDrill;
  // Read only from a SciChart view change (never during render), so it's kept current after
  // each commit rather than written while rendering.
  const onExtendRef = useRef(onExtend);
  useEffect(() => {
    onExtendRef.current = onExtend;
  }, [onExtend]);
  // What the once-created view-change handler reads of the latest draw (set in renderData, which
  // also moves the view): whether it's dense or extendable, and whether the last drag (or draw)
  // left the view's right edge past the end — so one drag past it asks for more bars once.
  const denseRef = useRef(false);
  const extendableRef = useRef(false);
  const pastEndRef = useRef(false);
  // The surface's axes and zoom/pan modifiers, created once in initChart and tuned per render.
  const xAxisRef = useRef<NumericAxis | null>(null);
  const zoomModifiersRef = useRef<ViewModifiers | null>(null);
  // Where Reset zoom returns the x-axis: every bar, or the opening window.
  const homeRangeRef = useRef<NumberRange | null>(null);
  // The window the last render drew, so more bars for the same window keep the user's view.
  const lastWindowRef = useRef<{
    windowed: boolean;
    windowKey: string | undefined;
  } | null>(null);

  const { rotated, dense } = useMemo(() => waterfallLayout(columns), [columns]);
  // Drags along x when there's more than fits, or more to come (zooming only when dense).
  const pannable = dense || initialVisibleCount !== undefined || extendable;
  const [zoomed, setZoomed] = useState(false);

  const [hover, setHover] = useState<HoverState | null>(null);
  // The surface's image exporter, handed over once it's created (null until then and on teardown).
  const [exporter, setExporter] = useState<ChartImageExporter | null>(null);

  // The drawer holds the chart and title it was opened with, so close it (when it's open for this
  // chart) once they go stale: the chart unmounts (a drill, a tab switch, leaving the page), its
  // surface is torn down, or what it shows is retitled (a reserve-category switch redraws the same
  // surface).
  const closeExportFor = useSetAtom(closeExportImageForAtom);
  const exportLabel = exportConfig?.label;
  const exportFilenamePrefix = exportConfig?.filenamePrefix;
  useEffect(() => {
    if (!exporter) return undefined;
    return () => closeExportFor(exporter);
  }, [closeExportFor, exporter, exportLabel, exportFilenamePrefix]);

  const initChart = ({ surface, wasmContext }: SciChartInitContext) => {
    const xAxis = new NumericAxis(wasmContext, {
      id: AXIS_ID_X,
      axisAlignment: EAxisAlignment.Bottom,
      autoTicks: false,
      majorDelta: 1,
      minorDelta: 1,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      labelStyle: { fontSize: 11 },
    });
    // Slot bars' long well labels are shortened on the axis (the tooltip shows them whole).
    xAxis.labelProvider.formatLabel = (value) => {
      const column = columnsRef.current[Math.round(value)];
      return column ? waterfallAxisLabel(column.key, column.label) : '';
    };
    // A position's bar changes as slots are revealed (and on a category switch), so its label is
    // looked up on every draw rather than taken from SciChart's per-position text cache, which
    // would keep showing the bar that used to be there.
    xAxis.labelProvider.textVariesForSameTick = true;
    surface.xAxes.add(xAxis);

    // No gridlines on either axis (INS-12792): the bars carry their own value labels.
    const yAxis = new NumericAxis(wasmContext, {
      id: AXIS_ID_Y,
      axisAlignment: EAxisAlignment.Left,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      labelStyle: { fontSize: 11 },
      labelPrecision: 0,
    });
    yAxis.labelProvider.formatLabel = waterfallValueAxisLabel;
    surface.yAxes.add(yAxis);

    const interaction = new BarInteractionModifier();
    interaction.getColumns = () => columnsRef.current;
    interaction.onDrill = (key) => onDrillRef.current?.(key);
    interaction.onHover = (index, x, y) =>
      setHover(index === null ? null : { index, x, y });
    surface.chartModifiers.add(interaction);

    // X-only zoom (mouse wheel) and pan (drag), switched on per render (see renderData). Added after
    // the bar interaction, which claims a drill's mousedown first.
    // Ctrl only: a plain wheel is left to scroll the page (the modifier then doesn't take the event).
    const wheelZoom = new MouseWheelZoomModifier({
      xyDirection: EXyDirection.XDirection,
      executeCondition: { key: EModifierMouseArgKey.Ctrl },
    });
    const pan = new ZoomPanModifier({ xyDirection: EXyDirection.XDirection });
    zoomModifiersRef.current = { wheelZoom, pan };
    [wheelZoom, pan].forEach((modifier) => {
      modifier.isEnabled = false;
      surface.chartModifiers.add(modifier);
    });
    // Ctrl + wheel off the plot (over the value axis or a margin) isn't the wheel zoom's to take, so
    // the browser would zoom the whole page instead; a chart that zooms on that gesture keeps it.
    surface.domChartRoot.addEventListener(
      'wheel',
      (event) => {
        if (event.ctrlKey && denseRef.current) event.preventDefault();
      },
      { passive: false }
    );

    // Each view change: fit a dense chart's value axis to the bars in view, flag a view moved off
    // home (for Reset zoom), and — on an extendable chart — ask for more bars when a drag takes the
    // view's right edge past the last bar. A drag keeps the view's span; a zoom changes it, and
    // a zoom into the room past the end asks for nothing until the next drag.
    xAxisRef.current = xAxis;
    let lastSpan: number | null = null;
    xAxis.visibleRangeChanged.subscribe(() => {
      const range = xAxis.visibleRange;
      const span = range.max - range.min;
      const dragged =
        lastSpan !== null &&
        Math.abs(span - lastSpan) <= Math.abs(span) * DRAG_SPAN_TOLERANCE;
      lastSpan = span;
      if (denseRef.current) {
        const { min, max } = visibleValueRange(
          columnsRef.current,
          range.min,
          range.max
        );
        yAxis.visibleRange = new NumberRange(min, max);
      }
      const home = homeRangeRef.current;
      if (home) {
        const tolerance = Math.abs(home.max - home.min) * ZOOM_TOLERANCE;
        setZoomed(
          Math.abs(range.min - home.min) > tolerance ||
            Math.abs(range.max - home.max) > tolerance
        );
      }
      const pastEnd =
        extendableRef.current &&
        pastTheEnd(range.max, columnsRef.current.length);
      if (dragged) {
        if (pastEnd && !pastEndRef.current) onExtendRef.current?.();
        pastEndRef.current = pastEnd;
      } else if (!pastEnd) {
        pastEndRef.current = false;
      }
    });
  };

  /** Returns the x-axis to its home: every bar, or the opening window. */
  const resetZoom = () => {
    const home = homeRangeRef.current;
    if (xAxisRef.current && home) {
      xAxisRef.current.visibleRange = new NumberRange(home.min, home.max);
    }
  };

  const renderData = ({ surface }: SciChartInitContext) => {
    const cols = columnsRef.current;
    surface.annotations.clear(true); // true: delete the native annotations too
    if (cols.length === 0) return;

    const xAxis = surface.xAxes.getById(AXIS_ID_X);
    const yAxis = surface.yAxes.getById(AXIS_ID_Y);

    denseRef.current = dense;
    extendableRef.current = extendable;
    const windowed = initialVisibleCount !== undefined;
    const fullRange = waterfallXRange(cols.length);
    const limit = panLimit(cols.length, extendable);
    const home = windowed
      ? waterfallXRange(Math.min(initialVisibleCount, cols.length))
      : fullRange;
    // More bars for the same window (a page of slots arriving) keep the user's view; anything else —
    // the first render, another window, a chart that isn't windowed — opens on home.
    const last = lastWindowRef.current;
    const keepView =
      windowed &&
      last !== null &&
      last.windowed &&
      last.windowKey === windowKey;
    lastWindowRef.current = { windowed, windowKey };
    homeRangeRef.current = home;
    setViewInteraction(zoomModifiersRef.current, dense, pannable);

    const loMin = Math.min(0, ...cols.map((c) => c.loM));
    const hiMax = Math.max(...cols.map((c) => c.hiM));
    const span = hiMax - loMin || 1;
    if (xAxis) {
      xAxis.labelProvider.rotation = rotated ? ROTATED_LABEL_DEGREES : 0;
      // Upright labels get narrower side padding (SciChart's default is 2 4 2 4), so more of them
      // fit before overlapping.
      xAxis.labelStyle = {
        padding: rotated
          ? new Thickness(2, 1, 2, 1)
          : new Thickness(2, 4, 2, 4),
      };
      // Pan and zoom stop at the first and last bar (or the room past it); a kept view is clipped to
      // the limit there is.
      xAxis.visibleRangeLimit = limit;
      const current = xAxis.visibleRange;
      xAxis.visibleRange = keepView
        ? new NumberRange(
            Math.max(current.min, limit.min),
            Math.min(current.max, limit.max)
          )
        : home;
      // Bars added under a kept view move the end on without changing the view — and SciChart
      // reports no change then — so where the edge sits against the new end is read here.
      pastEndRef.current =
        extendable && pastTheEnd(xAxis.visibleRange.max, cols.length);
    }
    if (yAxis) {
      // A dense chart's value axis follows the bars in view; any other grounds the anchors at $0.
      const valueRange =
        dense && xAxis
          ? visibleValueRange(
              cols,
              xAxis.visibleRange.min,
              xAxis.visibleRange.max
            )
          : {
              min: loMin - span * VALUE_AXIS_PAD_BELOW,
              max: hiMax + span * VALUE_AXIS_PAD_ABOVE,
            };
      yAxis.visibleRange = new NumberRange(valueRange.min, valueRange.max);
    }
    const labelPad = span * 0.03;

    cols.forEach((column, index) => {
      // Connector from the previous bar's running total into this delta bar's start.
      // (A dense chart's bars sit too close for connectors to read, so it skips them.)
      const previous = cols[index - 1];
      if (!dense && previous && column.tone !== 'absolute') {
        surface.annotations.add(
          new LineAnnotation({
            x1: index - 1 + HALF_BAR_WIDTH,
            y1: previous.runningM,
            x2: index - HALF_BAR_WIDTH,
            y2: column.startM,
            stroke: CONNECTOR_COLOR,
            strokeThickness: 1,
            strokeDashArray: [2, 2],
          })
        );
      }

      // The floating bar.
      surface.annotations.add(
        new BoxAnnotation({
          x1: index - HALF_BAR_WIDTH,
          x2: index + HALF_BAR_WIDTH,
          y1: column.loM,
          y2: column.hiM,
          fill: TONE_COLORS[column.tone],
          stroke: 'transparent',
          strokeThickness: 0,
        })
      );

      // Value label above (increase/anchor) or below (decrease) the bar. A dense chart labels none:
      // its labels would overlap, and its value axis follows the view, so a label's offset wouldn't
      // hold (the tooltip gives each value).
      const goesUp = column.tone === 'absolute' || column.valueM >= 0;
      if (!dense) {
        surface.annotations.add(
          new TextAnnotation({
            x1: index,
            y1: goesUp ? column.hiM + labelPad : column.loM - labelPad,
            text:
              column.tone === 'absolute'
                ? formatMillions(column.valueM * 1_000_000)
                : formatBarChange(column.valueM * 1_000_000),
            fontSize: 11,
            fontWeight: 'bold',
            textColor: TONE_COLORS[column.tone],
            horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
            verticalAnchorPoint: goesUp
              ? EVerticalAnchorPoint.Bottom
              : EVerticalAnchorPoint.Top,
          })
        );
      }

      // Drill affordance: a tone-colored ▾ above the bar (stacked over the value label
      // when that label is also above the bar), so it stays visible even on a near-$0
      // sliver — where the old white glyph drawn inside the bar top had no colored area
      // to sit on and vanished against the canvas. The x-based hit test is unchanged, so
      // the bar stays clickable regardless. Keyboard users drill through the bar's "Drill into"
      // button instead.
      if (column.drillable) {
        const drillPad = goesUp ? labelPad + span * 0.05 : labelPad;
        surface.annotations.add(
          new TextAnnotation({
            x1: index,
            y1: column.hiM + drillPad,
            text: '▾',
            fontSize: 12,
            fontWeight: 'bold',
            textColor: TONE_COLORS[column.tone],
            horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
            verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
          })
        );
      }
    });
  };

  // Static (no values), so the skeleton shows it too — the loaded chart then takes exactly the
  // space the skeleton held.
  const legend = (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <span
          className={styles.swatch}
          style={{ background: TONE_COLORS.absolute }}
        />
        Absolute value
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.swatch}
          style={{ background: TONE_COLORS.positive }}
        />
        Positive delta
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.swatch}
          style={{ background: TONE_COLORS.negative }}
        />
        Negative delta
      </span>
      {drillableKeys.length > 0 && (
        <span className={styles.legendDrill}>
          ▾ bars are drillable — click to expand
        </span>
      )}
      {dense && (
        <span className={styles.legendHint}>
          Ctrl + scroll to zoom · drag to pan
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.chart} aria-busy="true">
        {/* Height follows the chart's own `height` prop — a dynamic value. */}
        <SkeletonPlaceholder className={styles.skeleton} style={{ height }} />
        {legend}
      </div>
    );
  }

  if (bars.length === 0) return null;

  const hoveredColumn = hover ? columns[hover.index] : null;
  const drillableColumns = columns.filter((column) => column.drillable);

  return (
    <div className={styles.chart}>
      <div
        role="img"
        aria-label="NPV value-driver waterfall"
        onMouseLeave={() => setHover(null)}
      >
        <SciChartView
          className={pannable ? styles.pannable : undefined}
          initChart={initChart}
          renderData={renderData}
          renderDeps={[columns, initialVisibleCount, windowKey, extendable]}
          height={rotated ? height + ROTATED_LABEL_BAND_PX : height}
          onExporterReady={setExporter}
        >
          {hoveredColumn && hover && (
            <div
              className={styles.tooltip}
              style={{ left: hover.x + 12, top: hover.y + 12 }}
            >
              <div className={styles.tooltipTitle}>{hoveredColumn.label}</div>
              <div className={styles.tooltipRow}>
                {hoveredColumn.tone === 'absolute'
                  ? formatMillions(hoveredColumn.valueM * 1_000_000)
                  : `Δ ${formatBarChange(hoveredColumn.valueM * 1_000_000)}`}
              </div>
              {hoveredColumn.tone !== 'absolute' && (
                <div className={styles.tooltipRunning}>
                  Running {formatMillions(hoveredColumn.runningM * 1_000_000)}
                </div>
              )}
              {hoveredColumn.drillable && (
                <div className={styles.tooltipDrill}>Click to drill in ↗</div>
              )}
            </div>
          )}
        </SciChartView>
        {legend}
      </div>
      {/* Outside the image, whose content assistive tech doesn't reach. Each shows over the
          chart's corner only while focused, so the chart looks the same to mouse users. */}
      {drillableColumns.length > 0 && (
        <div className={styles.keyboardDrill}>
          {drillableColumns.map((column) => (
            <Button
              key={column.key}
              kind="tertiary"
              size="sm"
              className={styles.keyboardDrillButton}
              onClick={() => onDrill?.(column.key)}
            >
              Drill into {column.label}
            </Button>
          ))}
        </div>
      )}
      {/* Outside the image (SciChartView's own Reset Zoom would render inside it, under the export
          icon), left of the export icon. */}
      {pannable && zoomed && (
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Reset}
          className={classNames(styles.resetZoom, {
            [styles.resetZoomBesideExport]: !!(exportConfig && exporter),
          })}
          onClick={resetZoom}
        >
          Reset zoom
        </Button>
      )}
      {/* Outside the image too (SciChartView's own export trigger would render inside it), and
          after the drill buttons, so keyboard focus follows the visual left-to-right order. */}
      {exportConfig && exporter && (
        <ExportImageButton
          exporter={exporter}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          supportedFormats={EXPORT_FORMATS}
          iconOnly
          placement="overlay"
        />
      )}
    </div>
  );
}
